import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { formatCrewStat } from "@/lib/format-crew-stat"
import type { FeedStats } from "@/types/feed"

/**
 * OKS-509 — crew-campaign stats band at the top of the feed (UX Spec §2 top).
 *
 * Two rows: CREW totals (participations · crewmates · views · likes ·
 * comments) and a personal "YOU" contribution row (your posts · views ·
 * likes). Counts only — never another brand's named data (brand isolation P0;
 * this surface is single-brand by construction).
 *
 * Empty crew (`crew.participations === 0`) still renders with zeros — the
 * page shows the "be the first" empty feed beneath; the band itself is the
 * always-present social-proof frame.
 *
 * Locked testids: `feed-stats-band`, `feed-stats-crew`, `feed-stats-personal`.
 */
interface FeedStatsBandProps {
  stats: FeedStats
}

export function FeedStatsBand({ stats }: FeedStatsBandProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "fr"
  const fmt = (n: number) => formatCrewStat(n, locale)

  return (
    <Card data-testid="feed-stats-band">
      <CardContent className="flex flex-col gap-3 p-4">
        <div
          data-testid="feed-stats-crew"
          className="grid grid-cols-3 gap-3 sm:grid-cols-5"
        >
          <Stat
            label={t("crew_feed.stats.participations")}
            value={fmt(stats.crew.participations)}
          />
          <Stat
            label={t("crew_feed.stats.crewmates")}
            value={fmt(stats.crew.crewmates)}
          />
          <Stat
            label={t("crew_feed.stats.views")}
            value={fmt(stats.crew.views)}
          />
          <Stat
            label={t("crew_feed.stats.likes")}
            value={fmt(stats.crew.likes)}
          />
          <Stat
            label={t("crew_feed.stats.comments")}
            value={fmt(stats.crew.comments)}
          />
        </div>

        <div
          data-testid="feed-stats-personal"
          className="border-border/60 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 text-sm"
        >
          <span className="text-foreground font-semibold">
            {t("crew_feed.stats.you")}
          </span>
          <PersonalStat
            label={t("crew_feed.stats.your_posts")}
            value={fmt(stats.personal.participations)}
          />
          <PersonalStat
            label={t("crew_feed.stats.views")}
            value={fmt(stats.personal.views)}
          />
          <PersonalStat
            label={t("crew_feed.stats.likes")}
            value={fmt(stats.personal.likes)}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-base font-bold tabular-nums">{value}</span>
      <span className="text-muted-foreground text-xs">{label}</span>
    </div>
  )
}

function PersonalStat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-muted-foreground flex items-baseline gap-1">
      <span className="text-foreground font-semibold tabular-nums">
        {value}
      </span>
      {label}
    </span>
  )
}
